import { useState, useEffect, useRef, ChangeEvent } from "react";
import { Range, type Editor as TipTapEditorType } from "@tiptap/react";
import { Level } from "@tiptap/extension-heading";
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  Heading1Icon,
  ImageIcon,
  Heading2Icon,
  Heading3Icon,
  Table as TableIcon
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Card,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@libs/zapi-ui/shadcn";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { AppButton } from "@libs/zapi-ui/custom";
import { ChatCircleTextIcon, DotsThreeIcon } from "@phosphor-icons/react";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";

type TEditorToolbarProps = {
  editor: TipTapEditorType | null;
  fileName?: string;
  showCommentButton?: boolean;
  onCommentClick?: () => void;
};

const TOOLBAR_COLLAPSE_WIDTH = 500;

// type Selection = EditorState["selection"];

export const EditorToolbar = (props: TEditorToolbarProps) => {
  const { editor, fileName, showCommentButton = false, onCommentClick } = props;
  const { id } = useParams();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activeStates, setActiveStates] = useState({
    bold: false,
    italic: false,
    underline: false,
    link: false,
    heading1: false,
    heading2: false,
    heading3: false
  });

  const [storedSelection, setStoredSelection] = useState<Range | null>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const projectId = typeof id === "string" ? id : undefined;
  const isLaptop = useMediaQuery("(min-width: 984px)");

  useEffect(() => {
    if (!editor) return;
    const updateActiveStates = () => {
      setActiveStates({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        link: editor.isActive("link"),
        heading1: editor.isActive("heading", { level: 2 }),
        heading2: editor.isActive("heading", { level: 3 }),
        heading3: editor.isActive("heading", { level: 4 })
      });
    };

    editor.on("selectionUpdate", updateActiveStates);
    editor.on("transaction", updateActiveStates);
    updateActiveStates();

    return () => {
      editor.off("selectionUpdate", updateActiveStates);
      editor.off("transaction", updateActiveStates);
    };
  }, [editor]);

  useEffect(() => {
    if (!toolbarRef.current || typeof ResizeObserver === "undefined") return;

    const { current } = toolbarRef;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setIsCompact(entry.contentRect.width < TOOLBAR_COLLAPSE_WIDTH);
    });

    setIsCompact(current.offsetWidth < TOOLBAR_COLLAPSE_WIDTH);
    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!editor) return null;

  const handleImageButtonClick = () => {
    setStoredSelection(editor.state.selection);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const promises = files.map(async (tfile) => {
      const newFileName = `${fileName?.split(".")[0]}-${projectId}-${Date.now()}.png`;
      const file = new File([tfile], newFileName, { type: tfile.type });

      const res = await $api
        .post(`/projects/${projectId}/public-assets`, { file }, { headers: { "Content-Type": "multipart/form-data" } })
        .catch(() => {
          toast.error("Failed to upload image");
        });
      const url = res?.data?.data?.url;
      if (!url) {
        toast.error("Failed to upload image");
        return;
      }
      const createdNodeImage = editor?.schema.nodes.image.create({ src: url });
      if (createdNodeImage) {
        editor.commands.setTextSelection(storedSelection ?? 0);
        const transaction = editor.view.state.tr.replaceSelectionWith(createdNodeImage);
        editor.view.dispatch(transaction);
        return true;
      }
      return true;
    });

    await Promise.all(promises);

    e.target.value = "";
    setStoredSelection(null);
  };

  const handleSetLink = () => {
    if (editor) {
      setStoredSelection(editor.state.selection);
      setIsLinkDialogOpen(true);
    }
  };

  const onLinkSubmit = () => {
    const linkWithProtocol = linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}`;
    if (linkUrl) {
      editor.commands.setTextSelection(storedSelection ?? 0);
      editor.chain().extendMarkRange("link").setLink({ href: linkWithProtocol, target: "_blank" }).run();
    }
    setLinkUrl("");
    setIsLinkDialogOpen(false);
  };

  const handleToggleHeading = (level: Level) => {
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const handleInsertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();
  };

  return (
    <>
      <div ref={toolbarRef}>
        {/* desktop toolbar */}
        {isLaptop && (
          <Card
            data-toolbar="true"
            className="flex flex-row rounded-lg shadow-zinc-200 p-2 gap-2 items-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e.nativeEvent as any)["isToolbar"] = true;
            }}
          >
            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.bold ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleBold().run()}
              startAdornment={<Bold strokeWidth={3} />}
            />

            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.italic ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              startAdornment={<Italic className="h-4 w-4" />}
            />

            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.underline ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              startAdornment={<Underline className="h-4 w-4" />}
            />
            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.heading1 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(2)}
              startAdornment={<Heading1Icon className="h-4 w-4" />}
            />
            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.heading2 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(3)}
              startAdornment={<Heading2Icon className="h-4 w-4" />}
            />
            <AppButton
              appearance="text"
              size="icon"
              className={activeStates.heading3 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(4)}
              startAdornment={<Heading3Icon className="h-4 w-4" />}
            />
            {!isCompact && (
              <>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton appearance="text" onClick={handleImageButtonClick}>
                  <ImageIcon className="h-4 w-4" />
                  Image
                </AppButton>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton appearance="text" className={activeStates.link ? "bg-gray-200" : ""} onClick={handleSetLink}>
                  <LinkIcon className="h-4 w-4" />
                  Link
                </AppButton>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton appearance="text" onClick={handleInsertTable}>
                  <TableIcon className="h-4 w-4" />
                  Table
                </AppButton>
                {showCommentButton && onCommentClick && (
                  <>
                    <div className="h-4 w-[1px] bg-gray-200" />
                    <AppButton className="toolbar-comment-button" appearance="text" onClick={onCommentClick}>
                      Comment
                    </AppButton>
                  </>
                )}
              </>
            )}

            {isCompact && (
              <>
                <div className="h-4 w-[1px] bg-gray-200" />
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger data-testid="toolbar-trigger">
                    <AppButton appearance="text" size="icon" startAdornment={<DotsThreeIcon className="h-4 w-4" />} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="px-1 mr-5">
                    <DropdownMenuItem
                      data-testid="toolbar-image-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleImageButtonClick}
                    >
                      <ImageIcon className="h-4 w-4" />
                      Image
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="toolbar-link-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleSetLink}
                    >
                      <LinkIcon className="h-4 w-4" />
                      Link
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="toolbar-table-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleInsertTable}
                    >
                      <TableIcon className="h-4 w-4" />
                      Table
                    </DropdownMenuItem>
                    {showCommentButton && onCommentClick && (
                      <DropdownMenuItem
                        className="toolbar-comment-button cursor-pointer font-medium px-3 py-2"
                        variant="default"
                        onSelect={(e) => {
                          e.preventDefault();
                          setIsDropdownOpen(false);
                          onCommentClick();
                        }}
                      >
                        <ChatCircleTextIcon className="h-4 w-4" />
                        Comment
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              multiple
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Card>
        )}
        {/* tablet and mobile toolbar */}
        {!isLaptop && (
          <Card
            data-toolbar="true"
            className="flex flex-row bg-white p-2 gap-2 items-center overflow-hidden shadow-none"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onMouseUp={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e.nativeEvent as any)["isToolbar"] = true;
            }}
          >
            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.bold ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleBold().run()}
              startAdornment={<Bold strokeWidth={3} />}
            />

            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.italic ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              startAdornment={<Italic className="h-4 w-4" />}
            />

            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.underline ? "bg-gray-200" : ""}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              startAdornment={<Underline className="h-4 w-4" />}
            />
            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.heading1 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(2)}
              startAdornment={<Heading1Icon className="h-4 w-4" />}
            />
            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.heading2 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(3)}
              startAdornment={<Heading2Icon className="h-4 w-4" />}
            />
            <AppButton
              data-toolbar="true"
              appearance="text"
              size="icon"
              className={activeStates.heading3 ? "bg-gray-200" : ""}
              onClick={() => handleToggleHeading(4)}
              startAdornment={<Heading3Icon className="h-4 w-4" />}
            />
            {!isCompact && (
              <>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton data-toolbar="true" appearance="text" onClick={handleImageButtonClick}>
                  <ImageIcon className="h-4 w-4" />
                  Image
                </AppButton>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton
                  data-toolbar="true"
                  appearance="text"
                  className={activeStates.link ? "bg-gray-200" : ""}
                  onClick={handleSetLink}
                >
                  <LinkIcon className="h-4 w-4" />
                  Link
                </AppButton>
                <div className="h-4 w-[1px] bg-gray-200" />
                <AppButton data-toolbar="true" appearance="text" onClick={handleInsertTable}>
                  <TableIcon className="h-4 w-4" />
                  Table
                </AppButton>
              </>
            )}

            {isCompact && (
              <>
                <div className="h-4 w-[1px] bg-gray-200" />
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger data-testid="toolbar-trigger" data-toolbar="true">
                    <AppButton
                      data-toolbar="true"
                      appearance="text"
                      size="icon"
                      startAdornment={<DotsThreeIcon className="h-4 w-4" />}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent data-toolbar="true" className="px-1 mr-5">
                    <DropdownMenuItem
                      data-toolbar="true"
                      data-testid="toolbar-image-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleImageButtonClick}
                    >
                      <ImageIcon className="h-4 w-4" />
                      Image
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-toolbar="true"
                      data-testid="toolbar-link-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleSetLink}
                    >
                      <LinkIcon className="h-4 w-4" />
                      Link
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-toolbar="true"
                      data-testid="toolbar-table-button"
                      variant="default"
                      className="cursor-pointer font-medium px-3 py-2"
                      onSelect={handleInsertTable}
                    >
                      <TableIcon className="h-4 w-4" />
                      Table
                    </DropdownMenuItem>
                    {showCommentButton && onCommentClick && (
                      <DropdownMenuItem
                        data-toolbar="true"
                        className="toolbar-comment-button cursor-pointer font-medium px-3 py-2"
                        variant="default"
                        onSelect={(e) => {
                          e.preventDefault();
                          setIsDropdownOpen(false);
                          onCommentClick();
                        }}
                      >
                        <ChatCircleTextIcon className="h-4 w-4" />
                        Comment
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              multiple
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Card>
        )}
      </div>

      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent data-toolbar="true" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
            <DialogDescription>Enter the URL you want to link to.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-2">
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="border p-2 rounded-md"
            />
          </div>
          <DialogFooter>
            <AppButton appearance="text" onClick={() => setIsLinkDialogOpen(false)}>
              Cancel
            </AppButton>
            <AppButton onClick={onLinkSubmit}>Set Link</AppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
